import { InfraServiceName } from '@/pages/infra-service/types';
import { Customer } from '@/types/Customer';
import { Contract, PagedList } from '@/types/Facility';
import { State } from '@/types/State';
import { createSlice, Dispatch, PayloadAction } from '@reduxjs/toolkit';
import { findIndex } from 'lodash';
import { bffRequest } from '../../app/bffClient';
import {
  getContractDetails,
  getCustomerContracts,
  getGroupedCustomerContracts,
} from 'bff-api/mitt-malarenergi/v2';
import { getSelectedCustomer } from '../../app/slices/userSlice';

export type GroupedContracts = { [key in InfraServiceName]: Contract[] } | null;
export interface ContractsState {
  contractList: ContractList;
  groupedContracts: GroupedContracts;
}
export type ContractList = PagedList<Contract> | null;
export type ContractDetails = NonNullable<Contract['contractDetails']>;

interface PaginateOptions {
  page?: number;
  pageSize?: number;
}

export const contractSlice = createSlice({
  name: 'contracts',
  initialState: {
    contractList: null,
    groupedContracts: null,
  } as ContractsState,
  reducers: {
    setContracts: (state, action: PayloadAction<ContractList>) => {
      state.contractList = action.payload;
    },
    setGroupedContracts: (state, action: PayloadAction<GroupedContracts>) => {
      state.groupedContracts = action.payload;
    },
    setContractDetails: (
      state,
      action: PayloadAction<{
        contractId: string;
        contractDetails: ContractDetails;
      }>,
    ) => {
      if (!state.groupedContracts) return;

      const currentContract = action.payload.contractDetails.utilityType
        .name as InfraServiceName;
      const contractGroup = state.groupedContracts[currentContract];
      if (!contractGroup) return;

      const contractIndex = findIndex(
        contractGroup,
        (item) => item.contractId === action.payload.contractId,
      );
      if (contractIndex === -1) return;

      state.groupedContracts[currentContract][contractIndex] = {
        ...contractGroup[contractIndex],
        contractDetails: action.payload.contractDetails,
      };
    },
    setContractDetailsNotFound: (
      state,
      action: PayloadAction<{ contractId: string }>,
    ) => {
      if (!state.groupedContracts) return;

      const currentContract = Object.keys(state.groupedContracts).find(
        (utilityName) => {
          const contracts =
            state.groupedContracts?.[utilityName as InfraServiceName];
          return contracts?.find(
            (contract) => contract.contractId === action.payload.contractId,
          );
        },
      ) as InfraServiceName | undefined;

      if (!currentContract) return;

      const contractGroup = state.groupedContracts[currentContract];
      if (!contractGroup) return;

      const contractIndex = findIndex(
        contractGroup,
        (item) => item.contractId === action.payload.contractId,
      );
      if (contractIndex === -1) return;

      state.groupedContracts[currentContract][contractIndex] = {
        ...contractGroup[contractIndex],
        contractDetails: null,
      };
    },
  },
});

export const {
  setContracts,
  setContractDetails,
  setGroupedContracts,
  setContractDetailsNotFound,
} = contractSlice.actions;

export const fetchContracQuery = (
  facilityKey: string,
  infraService: string | null | undefined,
  selectedCustomer: Customer | null,
  groupId?: string,
  paginateOptions?: PaginateOptions,
): Promise<PagedList<Contract> | undefined> => {
  if (!selectedCustomer) {
    return Promise.resolve(undefined);
  }
  return bffRequest((client) =>
    getCustomerContracts({
      client,
      path: { customerId: selectedCustomer.customerId },
      query: {
        facilityKey,
        groupId,
        infraService: infraService ? infraService.toLowerCase() : undefined,
        ...paginateOptions,
      },
      throwOnError: true,
    }),
  ).then((envelope) => envelope.data?.[0] as PagedList<Contract> | undefined);
};

export const fetchContracts =
  (
    infraService: string | null | undefined,
    paginateOptions?: PaginateOptions,
  ) =>
  (dispatch: Dispatch, getState: () => State) => {
    const { selectedProperty } = getState().user;
    if (!selectedProperty) {
      return Promise.reject(new Error('No selected property'));
    }

    const { facilityKey, groupId } = selectedProperty;
    const selectedCustomer = getSelectedCustomer(getState());
    if (!selectedCustomer) {
      return Promise.reject(new Error('No selected customer'));
    }

    return fetchContracQuery(
      facilityKey,
      infraService,
      selectedCustomer,
      groupId,
      paginateOptions,
    ).then((contracts) => dispatch(setContracts(contracts ?? null)));
  };

export const fetchGroupedContracts =
  (infraService?: string | null | undefined) =>
  (dispatch: Dispatch, getState: () => State) => {
    const selectedCustomer = getSelectedCustomer(getState());
    if (!selectedCustomer) {
      return Promise.reject(new Error('No selected customer'));
    }

    return bffRequest((client) =>
      getGroupedCustomerContracts({
        client,
        path: { customerId: selectedCustomer.customerId },
        query: {
          infraService: infraService ? infraService.toLowerCase() : undefined,
        },
        throwOnError: true,
      }),
    )
      .then((envelope) => envelope.data?.[0])
      .then((contracts) =>
        dispatch(setGroupedContracts((contracts as GroupedContracts) ?? null)),
      );
  };

export const fetchContractDetails =
  (contractId: string) => (dispatch: Dispatch, getState: () => State) => {
    const selectedCustomer = getSelectedCustomer(getState());
    if (!selectedCustomer) {
      return Promise.reject(new Error('No selected customer'));
    }

    return bffRequest((client) =>
      getContractDetails({
        client,
        path: {
          customerId: selectedCustomer.customerId,
          contractId,
        },
        throwOnError: true,
      }),
    )
      .then((contractDetails) => {
        dispatch(
          setContractDetails({
            contractId,
            // The endpoint returns the details directly despite the generated
            // Swagger type describing a response envelope.
            contractDetails: contractDetails as unknown as ContractDetails,
          }),
        );
      })
      .catch(() => {
        dispatch(
          setContractDetailsNotFound({
            contractId,
          }),
        );
      });
  };

export default contractSlice.reducer;
